import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import moment from 'moment'

import useInterval from 'hooks/useInterval'

import { useAppDispatch, useAppSelector } from 'store'
import $app from 'store/app'
import $customer from 'store/customer'

import App from 'components/App'

import styles from './DialogCodeConfirm.module.scss'

export type Props = {
  open: boolean
  onSuccess: () => void
  onClose: () => void
}

const DialogCodeConfirm: React.FC<Props> = ({ open, onSuccess, onClose }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const phone = useAppSelector($customer.get.phone)
  const customerId = useAppSelector($customer.get.customerId)

  const [code, setCode] = useState(['', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState({
    minutes: '00',
    seconds: '00',
    minutesNumber: 0,
    secondsNumber: 0,
    isEnd: false,
  })

  const isMounted = useRef(true)

  const inputRef0 = useRef<HTMLInputElement>(null)
  const inputRef1 = useRef<HTMLInputElement>(null)
  const inputRef2 = useRef<HTMLInputElement>(null)
  const inputRef3 = useRef<HTMLInputElement>(null)

  const refs = { inputRef0, inputRef1, inputRef2, inputRef3 }
  const delayBetweenSms = 60 // is seconds

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (open && phone !== '') {
      sendCode()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone])

  useEffect(() => {
    if (open && inputRef0?.current) {
      inputRef0.current.focus()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (time >= 0) {
      setDuration(getDuration())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time])

  useEffect(() => {
    if (!isNotFullCode()) {
      handleCheckCode()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const getDuration = () => {
    const duration = moment.duration(time, 'seconds')
    const minutes = duration.minutes()
    const seconds = duration.seconds()

    return {
      minutes: (minutes > 9 ? minutes : `0${minutes}`).toString(),
      seconds: (seconds > 9 ? seconds : `0${seconds}`).toString(),
      minutesNumber: minutes,
      secondsNumber: seconds,
      isEnd: time <= 0,
    }
  }

  const tick = () => {
    setTime((prevTime) => prevTime - 1)
  }

  useInterval(tick, duration.isEnd ? null : 1000)

  const sendCode = async () => {
    try {
      const sentAt = localStorage.getItem('sentAt')
      if (sentAt) {
        const timestamp = JSON.parse(sentAt)
        const diff = moment().diff(moment(timestamp), 'seconds')
        if (diff < delayBetweenSms) {
          setTime(delayBetweenSms - diff)
          return 
        }
      }

      const result = await $customer.api.sendCode({ number: phone, customer_id: customerId })
      if (result && result.status === 'success') {
        const currentTimestamp = moment().valueOf()
        localStorage.setItem('sentAt', JSON.stringify(currentTimestamp))
        setTime(delayBetweenSms)
      } else {
        if (isMounted.current) {
          dispatch($app.set.error({ message: t(result?.message) }))
        }
      }
    } catch (error) {
      if (isMounted.current) {
        dispatch($app.set.error({ message: t('An unexpected error occurred while sending the code.') }));
        setError(t('Failed to send code. Please try again.'));
      }

      Sentry.captureException(error, {
        extra: { phone },
      })
    }
  }

  const handleCode = (index: number) => (value: string) => {
    if (value.length > 1) value = value.slice(0, 1)

    setError('')
    setCode((prevCode) => {
      const newCode = [...prevCode]
      newCode[index] = value

      return newCode
    })

    if (value !== '' && index < code.length - 1) {
      const nextInputRef = `inputRef${index + 1}` as keyof typeof refs
      refs[nextInputRef]?.current?.focus()
    } else if (value === '' && index > 0) {
      const prevInputRef = `inputRef${index - 1}` as keyof typeof refs
      refs[prevInputRef]?.current?.focus()
    }
  }

  const handlePaste = async (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()

    const pastedData = event.clipboardData.getData('text')
    if (/^\d{4}$/.test(pastedData)) {
      setCode([pastedData[0], pastedData[1], pastedData[2], pastedData[3]])
      inputRef3?.current?.focus()
    }
  }

  const isNotFullCode = () => {
    return code.some((value) => value === '')
  }

  const handleCheckCode = async () => {
    if (isNotFullCode()) return

    try {
      setLoading(true)

      const result = await $customer.api.checkCode({ number: phone, customer_id: customerId, code: code.join('') })
      if (result && result.status === 'success') {
        if (result.check) {
          onSuccess()
          dispatch($customer.set.confirmed(true))
          dispatch($app.set.phone(phone))
          onClose()
        } else {
          if (isMounted.current) {
            setError(t('Wrong confirmation code'))
          }
        }
      } else {
        if (isMounted.current) {
          dispatch($app.set.error({ message: t(result?.message || 'Failed to verify code') }))
        }
      }
    } catch (error) {
      if (isMounted.current) {
        dispatch($app.set.error({ message: t('An unexpected error occurred while verifying the code.') }))
        setError(t('Failed to verify code. Please try again'))
      }

      Sentry.captureException(error, {
        extra: { phone, code: code.join('') },
      })
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }
  
  return open ? (
    <App.Dialog noAnimation open={open} onClose={onClose}>
      <App.Flex column gap={6} className={styles.container}>
        <App.Flex column gap={3} aCenter>
          <App.Text center large bold>{t('Confirmation code')}</App.Text>
          <App.Text center muted>{t('Finish your registration by entering code from the SMS  sent to your phone number')}</App.Text>
        </App.Flex>

        <App.Flex column gap={1}>
          <App.Flex dir="ltr" row gap={4}>
            <App.Input ref={inputRef0} center flex={1} type="number" limit={1} value={code[0]} danger={error !== ''} onChange={handleCode(0)} onPaste={handlePaste} onEnter={handleCheckCode} />
            <App.Input ref={inputRef1} center flex={1} type="number" limit={1} value={code[1]} danger={error !== ''} onChange={handleCode(1)} onPaste={handlePaste} onEnter={handleCheckCode} />
            <App.Input ref={inputRef2} center flex={1} type="number" limit={1} value={code[2]} danger={error !== ''} onChange={handleCode(2)} onPaste={handlePaste} onEnter={handleCheckCode} />
            <App.Input ref={inputRef3} center flex={1} type="number" limit={1} value={code[3]} danger={error !== ''} onChange={handleCode(3)} onPaste={handlePaste} onEnter={handleCheckCode} />
          </App.Flex>

          {error !== '' ? (
            <App.Flex center>
              <App.Text center small danger>{error}</App.Text>
            </App.Flex>
          ) : null}
        </App.Flex>

        <App.Flex center>
          {duration.isEnd ? (
            <App.Text small highlight onClick={sendCode} style={{ cursor: 'pointer' }}>{t('Send code again')}</App.Text>
          ) : (
            <App.Flex row aCenter gap={1}>
              <App.Text small muted>{t('You can resend the code in')}</App.Text>

              <App.Flex row aCenter>
                <App.Text small muted>{duration.minutes}</App.Text>
                <App.Text small muted>:</App.Text>
                <App.Text small muted>{duration.seconds}</App.Text>
              </App.Flex>
            </App.Flex>
          )}
        </App.Flex>

        <App.Button loading={loading} disabled={isNotFullCode()} onClick={handleCheckCode}>{t('Confirm')}</App.Button>
      </App.Flex>
    </App.Dialog>
  ) : null
}

export default DialogCodeConfirm